<template>
  <div
    class="MaxPlayerCountInput"
    :class="{
      'MaxPlayerCountInput--shouldSlide': shouldSlide,
    }"
    :data-direction="direction"
  >
    <div
      class="MaxPlayerCountInput__header"
      data-testid="header"
      aria-hidden="true"
    >
      <Transition>
        <span
          :key="modelValue === null ? 0 : 1"
          class="MaxPlayerCountInput__headerTitle"
        >{{ formattedHeader }}</span>
      </Transition>
    </div>

    <div
      class="MaxPlayerCountInput__trackContainer"
    >
      <TransitionGroup
        ref="trackRef"
        class="MaxPlayerCountInput__track"
        tag="div"
      >
        <div
          v-for="(_item, i) in items"
          :key="i"
          class="MaxPlayerCountInput__trackTick"
          :data-size="i % interval === 0 ? 'large' : 'small'"
          :style="{
            left: `${i / range * trackWidth}px`
          }"
        />
      </TransitionGroup>

      <Basketball
        class="MaxPlayerCountInput__thumb"
        data-testid="thumb"
      />
    </div>

    <TransitionGroup
      class="MaxPlayerCountInput__tickLabelContainer"
      tag="div"
    >
      <div
        v-for="(tick, i) in ticks"
        :key="i"
        class="MaxPlayerCountInput__tickLabel"
        data-testid="tickLabel"
        :style="{
          left: `calc(${(tick.value - min) / range * trackWidth}px)`,
        }"
      >
        <div class="MaxPlayerCountInput__tickLabelValue">
          {{ tick.tick }}
        </div>
      </div>
      <div
        key="infinity"
        class="MaxPlayerCountInput__tickLabel"
        style="left: 100%;"
        aria-label="Infinity"
        data-testid="tickLabel"
      >
        <Icon
          class="MaxPlayerCountInput__tickLabelValue"
          name="infinity"
          size="medium"
        />
      </div>
    </TransitionGroup>

    <input
      v-model.number="inputModelValue"
      class="MaxPlayerCountInput__input"
      type="range"
      :min="min"
      :max="inputMax"
      :step="step"
      :aria-valuetext="formattedHeader"
    >
  </div>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import haptic from "@/lib/utils/platform/haptic";
import { ImpactStyle } from "@capacitor/haptics";
import { ComponentPublicInstance, computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Icon from "./Icon.vue";

const modelValue = defineModel<number | null>({
  required: true,
});

const {
  minPlayerCount,
} = defineProps<{
  minPlayerCount?: number | null,
}>();

const resizeObserver = ref<ResizeObserver | null>(null);
const trackRef = ref<ComponentPublicInstance | null>(null);
const trackWidth = ref(1);
const step = 1;

onMounted(() => {
  if (!trackRef.value?.$el) return;

  resizeObserver.value = new ResizeObserver(handleResize);
  resizeObserver.value.observe(trackRef.value.$el);
});

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect();
});

const min = computed(() => {
  return minPlayerCount ?? 2;
});

const max = computed(() => {
  switch (min.value) {
    case 4:
      return 20;
    case 6:
      return 21;
    case 8:
      return 24;
    case 10:
      return 25;
    default:
      return 20;
  }
});

const interval = computed(() => {
  switch (min.value) {
    case 4:
      return 4;
    case 6:
      return 3;
    case 8:
      return 4;
    case 10:
      return 5;
    default:
      return 6;
  }
});

const inputMax = computed(() => {
  return max.value + interval.value;
});

const range = computed(() => {
  return inputMax.value - min.value;
});

const inputModelValue = computed({
  get () {
    if (modelValue.value === null) return inputMax.value;

    return modelValue.value;
  },
  set (value: number | null) {
    if (value !== null && value <= max.value + interval.value / 2) {
      modelValue.value = Math.max(min.value, Math.min(max.value, value));
      return;
    }

    modelValue.value = null;
  },
});

const items = computed(() => {
  return Array.from({
    length: Math.floor((max.value - min.value) / step) + 1,
  }, (_value, i) => min.value + i * step);
});

const ticks = computed(() => {
  return Array.from({
    length: Math.floor((max.value - min.value) / interval.value) + 1,
  }, (_, i) => ({
    value: min.value + i * interval.value,
    tick: min.value + i * interval.value,
  }));
});

watch([min, max], () => {
  if (modelValue.value === null) return;

  modelValue.value = Math.max(min.value, Math.min(max.value, modelValue.value));
});

const direction = ref<"left" | "right">("left");
const shouldSlide = ref(false);

watch(modelValue, (newValue, oldValue) => {
  direction.value = newValue === null || (oldValue !== null && newValue > oldValue) ? "right" : "left";
  shouldSlide.value = newValue === null || oldValue === null || Math.abs(newValue - oldValue) >= 2;

  haptic(newValue === null || oldValue === null ? ImpactStyle.Heavy : ImpactStyle.Medium);
});

const formattedHeader = computed(() => {
  if (modelValue.value === null) return "No cap";

  return `${modelValue.value} players`;
});

const thumbPosition = computed(() => {
  if (modelValue.value === null) return range.value;

  return modelValue.value - min.value;
});

const thumbMax = computed(() => {
  return range.value;
});

function handleResize () {
  trackWidth.value = trackRef.value?.$el.clientWidth ?? 0;
}
</script>

<style lang="scss" scoped>
.MaxPlayerCountInput {
  --thumbRadius: 16px;
  --thumbSize: calc(2 * var(--thumbRadius));
  --inputThumbSize: calc(2 * (16px + var(--thumbRadius)));
  --inputHeight: 122px;
  position: relative;

  display: grid;
  padding: 20px 16px 16px;
  overflow: hidden;

  border: 2px solid var(--themeColorNeutral900);
  border-radius: 6px;
  background-color: var(--themeColorInputBackground);
  color: var(--themeColorInputForeground);
  box-shadow: 0 0 0 0 transparent;

  transition: 350ms;
  transition-property: box-shadow, border-color, background-color;

  &:has(:disabled) {
    border-color: var(--themeColorNeutral400);
    background-color: var(--themeColorNeutral0);
    color: var(--themeDarkTransparent500);
  }

  &:focus-within {
    box-shadow: 0 0 0 3px var(--themeColorSecondary);
  }
}

.MaxPlayerCountInput__header {
  position: relative;

  display: grid;
  place-items: center;
  margin-bottom: 20px;

  font: var(--fontWeightNormal) 18px/1 var(--fontFamilyMono);
}

.MaxPlayerCountInput__headerTitle {
  display: block;
  transition: 150ms;
  transition-property: opacity, transform;

  &.v-leave-active {
    position: absolute;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  .MaxPlayerCountInput[data-direction='right'] &.v-enter-from,
  .MaxPlayerCountInput[data-direction='left'] &.v-leave-to {
    transform: translateX(-24px);
  }

  .MaxPlayerCountInput[data-direction='right'] &.v-leave-to,
  .MaxPlayerCountInput[data-direction='left'] &.v-enter-from {
    transform: translateX(24px);
  }
}

.MaxPlayerCountInput__tickLabelContainer {
  position: relative;
  height: 1lh;
  margin: 8px var(--thumbRadius) 0;
}

.MaxPlayerCountInput__tickLabel {
  position: absolute;
  top: 0;
  left: 0;

  transition: 250ms;
  transition-property: left, opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.MaxPlayerCountInput__tickLabelValue {
  transform: translateX(-50%);
  font: var(--fontWeightNormal) 16px/1 var(--fontFamilyMono);
  text-align: center;
}

.MaxPlayerCountInput__input {
  position: absolute;
  inset: 0;

  outline: none;
  background: none;
  appearance: none;

  &::-webkit-slider-runnable-track {
    display: grid;
    align-items: center;
    height: var(--inputHeight);
  }

  &::-moz-range-track {
    display: grid;
    align-items: center;
    height: var(--inputHeight);
  }

  &::-webkit-slider-thumb {
    width: var(--inputThumbSize);
    height: var(--inputHeight);

    background-color: transparent;
    box-shadow: none;
    appearance: none;
  }

  &::-moz-range-thumb {
    width: var(--inputThumbSize);
    height: var(--inputHeight);

    border: none;
    border-radius: 0;
    background-color: transparent;
  }

  &:not(:disabled) {
    cursor: pointer;
  }
}

.MaxPlayerCountInput__trackContainer {
  position: relative;

  display: grid;
  align-items: center;
  height: 24px;

  pointer-events: none;
}

.MaxPlayerCountInput__track {
  position: relative;

  height: 6px;
  margin: 0 var(--thumbRadius);

  background-color: var(--themeColorNeutral150);

  &::after {
    --size: 18px;
    position: absolute;
    top: calc(50% - var(--size) / 2);
    left: calc(100% - var(--size) / 2);

    width: var(--size);
    height: var(--size);

    border-radius: 50%;
    background-color: inherit;

    content: "";
  }
}

.MaxPlayerCountInput__trackTick {
  position: absolute;
  top: 50%;

  transition: 250ms;
  transition-property: left, opacity;

  &.v-enter-from,
  &.v-leave-to {
    --scaleY: 0;
    opacity: 0;
  }

  &::before {
    transform-origin: top left;

    display: block;
    width: 100px;
    height: 100px;

    background-color: var(--themeColorNeutral150);

    transition: inherit;
    transition-property: transform;

    content: "";
  }

  &[data-size="small"] {
    &::before {
      transform: scaleX(0.02) scaleY(var(--scaleY, 0.16)) translate(-50%, -50%);
    }
  }

  &[data-size="large"] {
    &::before {
      transform: scaleX(0.03) scaleY(var(--scaleY, 0.24)) translate(-50%, -50%);
    }
  }
}

.MaxPlayerCountInput__thumb {
  position: absolute;
  top: calc(50% - var(--thumbRadius));
  left: calc((100% - var(--thumbSize)) * v-bind(thumbPosition) / v-bind(thumbMax));

  .MaxPlayerCountInput--shouldSlide & {
    transition: 150ms left;
  }

  .MaxPlayerCountInput:has(:disabled) & {
    --iconColor: var(--themeColorNeutral150);
    --strokeColor: var(--themeColorNeutral500);
  }
}
</style>
